import {
  For,
  Show,
  createEffect,
  createSignal,
} from "solid-js";
import { api } from "../api/client";
import type { Exercise, WorkoutTemplate } from "../api/types";
import { t } from "../i18n";
import { errorCountText, rowIssue } from "../lib/activity";
import { createCachedResource } from "../lib/cache";
import { useCatalog } from "../lib/data";
import { DURATION_PRESETS, formatDuration } from "../lib/duration";
import { newExercise, resolveRowsFromCatalog } from "../lib/exerciseRows";
import { cloneExercises } from "../lib/repeatWorkout";
import { withSortOrder } from "../lib/reorder";
import ExerciseEditor from "./ExerciseEditor";
import Section from "./tg/Section";

export interface WorkoutFormValue {
  title: string;
  notes: string;
  date: string;
  /** Planned workouts only — optional "HH:mm" */
  startTime?: string | null;
  exercises: Exercise[];
  /** Completed workouts only */
  durationMinutes?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
}

interface Props {
  mode: "completed" | "planned";
  initial?: Partial<WorkoutFormValue>;
  submitLabel?: string;
  onSubmit: (value: WorkoutFormValue) => Promise<void>;
  onCancel?: () => void;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function WorkoutForm(props: Props) {
  const [title, setTitle] = createSignal(props.initial?.title ?? "");
  const [notes, setNotes] = createSignal(props.initial?.notes ?? "");
  const [date, setDate] = createSignal(props.initial?.date ?? todayISO());
  const [startTime, setStartTime] = createSignal<string>(
    props.initial?.startTime ?? "",
  );
  const [exercises, setExercises] = createSignal<Exercise[]>(
    props.initial?.exercises?.length
      ? props.initial.exercises
      : [newExercise()],
  );
  const [showErrors, setShowErrors] = createSignal(false);
  const [catalog] = useCatalog();
  const [durationMinutes, setDurationMinutes] = createSignal<number | null>(
    props.initial?.durationMinutes ?? null,
  );
  const [startedAt] = createSignal<string | null>(
    props.initial?.startedAt ?? null,
  );
  const [finishedAt] = createSignal<string | null>(
    props.initial?.finishedAt ?? null,
  );
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [templates] = createCachedResource(() => true, {
    key: () => "templates",
    fetch: () => api.listTemplates(),
  });

  let errorEl: HTMLParagraphElement | undefined;

  createEffect(() => {
    if (error()) {
      queueMicrotask(() => {
        errorEl?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  });

  function applyTemplate(tpl: WorkoutTemplate) {
    setTitle(tpl.name);
    setExercises(cloneExercises(tpl.exercises, { copyNotes: true }));
  }

  function clearDuration() {
    setDurationMinutes(null);
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    setError(null);
    const resolved = resolveRowsFromCatalog(exercises(), catalog() ?? []);
    setExercises(resolved);
    const rows = resolved.filter((ex) => ex.name.trim());
    const issues = rows.filter((ex) => rowIssue(ex)).length;
    if (issues > 0) {
      setShowErrors(true);
      setError(`${t("FIELDS_INVALID")}: ${errorCountText(issues)}`);
      return;
    }
    setSaving(true);
    try {
      await props.onSubmit({
        title: title().trim(),
        notes: notes().trim(),
        date: date(),
        startTime:
          props.mode === "planned"
            ? startTime().trim() || null
            : undefined,
        exercises: withSortOrder(rows),
        durationMinutes:
          props.mode === "completed" ? durationMinutes() : undefined,
        startedAt: props.mode === "completed" ? startedAt() : undefined,
        finishedAt: props.mode === "completed" ? finishedAt() : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="pb-6" onSubmit={handleSubmit}>
      <Show when={templates()?.length}>
        <Section>
          <div class="px-4 py-3">
            <label class="label">{t("FROM_TEMPLATE")}</label>
            <select
              class="input"
              onChange={(e) => {
                const id = e.currentTarget.value;
                const tpl = templates()?.find((x) => x.id === id);
                if (tpl) applyTemplate(tpl);
                e.currentTarget.value = "";
              }}
            >
              <option value="">{t("APPLY_TEMPLATE")}…</option>
              <For each={templates()}>
                {(tpl) => <option value={tpl.id}>{tpl.name}</option>}
              </For>
            </select>
          </div>
        </Section>
      </Show>

      <Section>
        <div class="space-y-3 px-4 py-3">
          <div>
            <label class="label">{t("TITLE")}</label>
            <input
              class="input"
              required
              value={title()}
              onInput={(e) => setTitle(e.currentTarget.value)}
            />
          </div>

          <div>
            <label class="label">{t("DATE")}</label>
            <input
              class="input"
              type="date"
              required
              value={date()}
              onInput={(e) => setDate(e.currentTarget.value)}
            />
          </div>

          <Show when={props.mode === "planned"}>
            <div class="space-y-2">
              <div class="flex items-center justify-between gap-2">
                <label class="label !mb-0">
                  {t("START_TIME")}{" "}
                  <span class="font-normal text-ink-faint">({t("OPTIONAL")})</span>
                </label>
                <Show when={startTime()}>
                  <button
                    type="button"
                    class="btn-ghost text-xs py-1"
                    onClick={() => setStartTime("")}
                  >
                    {t("CLEAR")}
                  </button>
                </Show>
              </div>
              <input
                class="input"
                type="time"
                value={startTime()}
                onInput={(e) => setStartTime(e.currentTarget.value)}
                aria-label={t("START_TIME")}
              />
              <p class="text-[11px] text-ink-faint">{t("START_TIME_HINT")}</p>
            </div>
          </Show>

          <Show when={props.mode === "completed"}>
            <div class="space-y-3">
              <div class="flex items-center justify-between gap-2">
                <label class="label !mb-0">{t("DURATION")}</label>
                <Show when={durationMinutes() != null}>
                  <button
                    type="button"
                    class="btn-ghost text-xs py-1"
                    onClick={clearDuration}
                  >
                    {t("CLEAR")}
                  </button>
                </Show>
              </div>

              <div class="flex items-center gap-2">
                <input
                  class="input flex-1 tabular-nums"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="—"
                  value={durationMinutes() ?? ""}
                  onInput={(e) => {
                    const v = e.currentTarget.value;
                    if (v === "") {
                      setDurationMinutes(null);
                      return;
                    }
                    const n = Math.round(Number(v));
                    if (Number.isFinite(n) && n >= 1) setDurationMinutes(n);
                  }}
                  aria-label={t("DURATION")}
                />
                <span class="shrink-0 text-sm text-ink-muted">{t("DURATION_MIN")}</span>
              </div>

              <div class="flex flex-wrap gap-1.5">
                <For each={[...DURATION_PRESETS]}>
                  {(m) => (
                    <button
                      type="button"
                      class={
                        durationMinutes() === m ? "tg-chip is-active" : "tg-chip"
                      }
                      onClick={() => setDurationMinutes(m)}
                    >
                      {formatDuration(m) || `${m}`}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </Show>
        </div>
      </Section>

      <Section>
        <div class="px-4 py-3">
          <label class="label">
            {t("NOTES")} ({t("OPTIONAL")})
          </label>
          <textarea
            class="input min-h-[72px] resize-y"
            value={notes()}
            onInput={(e) => setNotes(e.currentTarget.value)}
          />
        </div>
      </Section>

      <ExerciseEditor
        exercises={exercises()}
        onChange={setExercises}
        showErrors={showErrors()}
      />

      <Show when={error()}>
        <p
          ref={errorEl}
          class="px-1 py-2 text-[15px] text-[var(--destructive)]"
        >
          {error()}
        </p>
      </Show>

      <div class="mt-4 flex gap-2">
        <Show when={props.onCancel}>
          <button
            type="button"
            class="btn-secondary flex-1"
            onClick={() => props.onCancel?.()}
          >
            {t("CANCEL")}
          </button>
        </Show>
        <button type="submit" class="btn-primary flex-1" disabled={saving()}>
          {saving() ? t("LOADING") : props.submitLabel ?? t("SAVE")}
        </button>
      </div>
    </form>
  );
}
